import type {QRCodeFileExtension, QRPayloadType} from '@Libraries/qr-code/qr-code.types';
import type {RGBA} from '@Utils/color.util';
import {GA4EventName, GA4EventParamName, trackGA4CustomEvent} from '@Libraries/ga-events';
import type {QRCodeStyle} from '@Components/qr-code-generator/qr-code-generator.types';

/** Where the code's logo came from. Only `UPLOAD` is a deliberate act; a platform mark is derived from the link that was typed. */
export enum QRLogoSource {
  UPLOAD = 'upload',
  PLATFORM = 'platform',
}

export enum QRCopyResult {
  OK = 'ok',
  FAILED = 'failed',
}

/** The style panel's own controls, named the way it labels them, so a report reads against the UI rather than against the style object. */
enum QRStyleFacet {
  PATTERN = 'pattern',
  CORNERS = 'corners',
  COLOUR = 'colour',
  OUTLINE = 'outline',
  ROUNDNESS = 'roundness',
  PADDING = 'padding',
}

const QR_STYLE_FACET_SEPARATOR = ',';

const isSameColor = (color: RGBA, otherColor: RGBA): boolean => {
  return color.every((channel, channelIndex): boolean => {
    return channel === otherColor[channelIndex];
  });
};

/**
 * Which of the panel's controls the visitor actually moved. Several controls write more than one style field — corners writes both eye
 * shapes, outline writes a colour and a thickness — so the diff is taken per control rather than per field.
 */
const getChangedStyleFacets = (previousStyle: QRCodeStyle, appliedStyle: QRCodeStyle): QRStyleFacet[] => {
  const changedFacets: QRStyleFacet[] = [];

  if (previousStyle.dotStyle !== appliedStyle.dotStyle) {
    changedFacets.push(QRStyleFacet.PATTERN);
  }
  if (previousStyle.eyeFrameStyle !== appliedStyle.eyeFrameStyle || previousStyle.eyeBallStyle !== appliedStyle.eyeBallStyle) {
    changedFacets.push(QRStyleFacet.CORNERS);
  }
  if (!isSameColor(previousStyle.foregroundColor, appliedStyle.foregroundColor) || !isSameColor(previousStyle.backgroundColor, appliedStyle.backgroundColor)) {
    changedFacets.push(QRStyleFacet.COLOUR);
  }
  if (previousStyle.outlineThickness !== appliedStyle.outlineThickness || !isSameColor(previousStyle.outlineColor, appliedStyle.outlineColor)) {
    changedFacets.push(QRStyleFacet.OUTLINE);
  }
  if (previousStyle.backgroundCornerRadiusPercent !== appliedStyle.backgroundCornerRadiusPercent) {
    changedFacets.push(QRStyleFacet.ROUNDNESS);
  }
  if (previousStyle.marginPercent !== appliedStyle.marginPercent) {
    changedFacets.push(QRStyleFacet.PADDING);
  }

  return changedFacets;
};

export const trackQRTypeSelected = (codeType: QRPayloadType): void => {
  trackGA4CustomEvent(GA4EventName.QR_TOOL_TYPE_SELECTED, {[GA4EventParamName.TYPE]: codeType});
};

/** Silent when the panel was opened and closed without moving anything: that is curiosity, and counting it as styling hides the real rate. */
export const trackQRStyleApplied = (codeType: QRPayloadType, previousStyle: QRCodeStyle, appliedStyle: QRCodeStyle): void => {
  const changedFacets = getChangedStyleFacets(previousStyle, appliedStyle);
  if (changedFacets.length === 0) {
    return;
  }

  trackGA4CustomEvent(GA4EventName.QR_TOOL_STYLE_APPLIED, {
    [GA4EventParamName.TYPE]: codeType,
    [GA4EventParamName.VALUE]: changedFacets.join(QR_STYLE_FACET_SEPARATOR),
  });
};

export const trackQRLogoAdded = (codeType: QRPayloadType, logoSource: QRLogoSource): void => {
  trackGA4CustomEvent(GA4EventName.QR_TOOL_LOGO_ADDED, {
    [GA4EventParamName.TYPE]: codeType,
    [GA4EventParamName.SOURCE]: logoSource,
  });
};

export const trackQRDownload = (codeType: QRPayloadType, fileExtension: QRCodeFileExtension): void => {
  trackGA4CustomEvent(GA4EventName.QR_TOOL_DOWNLOAD, {
    [GA4EventParamName.TYPE]: codeType,
    [GA4EventParamName.VALUE]: fileExtension,
  });
};

export const trackQRCopy = (codeType: QRPayloadType, copyResult: QRCopyResult): void => {
  trackGA4CustomEvent(GA4EventName.QR_TOOL_COPY, {
    [GA4EventParamName.TYPE]: codeType,
    [GA4EventParamName.RESULT]: copyResult,
  });
};

export const trackQRDesignSelected = (codeType: QRPayloadType): void => {
  trackGA4CustomEvent(GA4EventName.QR_TOOL_DESIGN_SELECTED, {[GA4EventParamName.TYPE]: codeType});
};

export const trackQRGalleryClick = (codeType: QRPayloadType): void => {
  trackGA4CustomEvent(GA4EventName.QR_TOOL_GALLERY_CLICK, {[GA4EventParamName.TYPE]: codeType});
};
